/**
 * API contract versioning — the shared source of truth for the transport-layer
 * version negotiation between every client (web, extension, Android, iOS) and
 * the server.
 *
 * See docs/superpowers/specs/2026-07-21-api-versioning-for-breaking-changes-design.md.
 *
 * Every request carries two headers:
 *   - `X-HBS-Api-Version: <int>`      — the highest contract version the client
 *                                       understands. A single monotonically
 *                                       increasing integer.
 *   - `X-HBS-Client: <platform>/<ver>` — the client's identity (telemetry only,
 *                                       never a gate): `web/1.8.2`, `android/3.4.0`.
 *
 * An ABSENT version header resolves to `LEGACY_API_VERSION` (v1) so every client
 * deployed before versioning existed keeps working unchanged. The server serves
 * a window `[MIN_SUPPORTED_API_VERSION, API_VERSION]`; a request below the floor
 * gets `426 Upgrade Required` (the one place an upgrade is forced).
 *
 * The native clients (Kotlin, Swift) mirror `API_VERSION` in their own constant;
 * when the server bumps it, all client constants + the server max move together
 * in the breaking change's PR.
 */

/** The newest contract version the server serves / the current client speaks. */
export const API_VERSION = 1;

/** Requests negotiating below this get 426 Upgrade Required. */
export const MIN_SUPPORTED_API_VERSION = 1;

/** What an absent/garbage `X-HBS-Api-Version` header resolves to. */
export const LEGACY_API_VERSION = 1;

/** Contract-version header (client → server, every request). */
export const HDR_API_VERSION = 'X-HBS-Api-Version';

/** Client-identity header (`<platform>/<semver>`) — telemetry only, never gated. */
export const HDR_CLIENT = 'X-HBS-Client';

/** Body of a `426 Upgrade Required` response. Mirrors the EntitlementErrorBody
 *  convention: a stable machine code + optional human message; clients render
 *  their own "please update the app" copy from the code. */
export interface UpgradeRequiredBody {
  code: 'UPGRADE_REQUIRED';
  /** The lowest contract version the server still serves. */
  minVersion: number;
  message?: string;
}
